<ng-container *transloco="let t; read: 'devices.wizard'">
  <app-wizard-modal
    #wizard
    [(open)]="open"
    [title]="mode === 'add' ? t('titleAdd') : t('titleEdit')"
    [subtitle]="mode === 'add' ? t('subtitleAdd') : t('subtitleEdit')"
    [saveExitLabel]="mode === 'edit' ? t('saveExit') : null"
    [busy]="busy"
    [confirmClose]="true"
    (finished)="onFinished()"
    (saveExit)="onFinished()"
    (closed)="onClose()"
  >
    <!-- 1 · Basics: client ID + location names -->
    <app-wizard-step
      id="basics"
      [label]="t('steps.basics')"
      [sublabel]="t('steps.basicsSub')"
      [description]="t('steps.basicsDesc')"
      icon="icon-menu-displays1"
      [control]="basicsForm"
    >
      <ng-template>
        <form [formGroup]="basicsForm" class="flex flex-col gap-6">
          <app-form-field
            [help]="t('fields.clientIdHelp')"
            [error]="basicsForm.controls.clientId.invalid && basicsForm.controls.clientId.touched ? t('errors.clientIdRequired') : null"
          >
            <app-text-input
              formControlName="clientId"
              [label]="t('fields.clientId')"
              [placeholder]="t('fields.clientIdPlaceholder')"
              [invalid]="basicsForm.controls.clientId.invalid && basicsForm.controls.clientId.touched"
            />
          </app-form-field>

          <div>
            <div class="mb-1 flex items-baseline justify-between">
              <span class="text-[13px] font-bold uppercase tracking-wide text-ink-2">{{ t('fields.locationNames') }}</span>
              <span class="text-[12px] text-ink-3">{{ t('fields.locationNamesNote') }}</span>
            </div>
            <div class="flex flex-col gap-2">
              @for (location of locationNames; track trackByIndex(i, location); let i = $index) {
                <div class="flex items-center gap-2">
                  <app-text-input
                    class="w-20 flex-none"
                    [(ngModel)]="location.lang"
                    [ngModelOptions]="{ standalone: true }"
                    [label]="t('fields.lang')"
                    [placeholder]="t('fields.langPlaceholder')"
                  />
                  <app-text-input
                    class="flex-1"
                    [(ngModel)]="location.text"
                    [ngModelOptions]="{ standalone: true }"
                    [label]="t('fields.locationName')"
                    [placeholder]="t('fields.locationName')"
                  />
                  <button type="button" (click)="removeLocationName(i)" class="flex-none px-1 text-[13px] font-semibold text-danger hover:underline">
                    &times;
                  </button>
                </div>
              }
            </div>
            <button type="button" (click)="addLocationName()" class="mt-2 self-start px-2 text-[13px] font-semibold text-link hover:underline">
              {{ t('fields.addLanguage') }}
            </button>
          </div>
        </form>
      </ng-template>
    </app-wizard-step>

    <!-- 2 · Feeds & stops -->
    <app-wizard-step
      id="feeds"
      [label]="t('steps.feeds')"
      [sublabel]="t('steps.feedsSub', { feeds: assignedFeeds().length, stops: totalStopCount })"
      [description]="t('steps.feedsDesc')"
      icon="icon-cloud"
    >
      <ng-template>
        <div class="mb-3 flex items-baseline justify-between">
          <span class="text-[13px] font-bold uppercase tracking-wide text-ink-2">{{ t('fields.assignedFeeds') }}</span>
          <div class="relative" data-feed-menu>
            <button type="button" (click)="toggleFeedMenu()" class="px-2 text-[13px] font-semibold text-link hover:underline">
              {{ t('fields.addFeed') }}
            </button>
            @if (isFeedMenuOpen()) {
              <div class="absolute right-0 z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded-control bg-card p-1 shadow-raised">
                @if (unassignedFeedOptions.length === 0) {
                  <div class="px-3 py-2 text-[13px] text-ink-3">{{ t('fields.noFeedsAvailable') }}</div>
                } @else {
                  @for (opt of unassignedFeedOptions; track opt.feed.id) {
                    <button type="button" (click)="addFeed(opt.feed, opt.providerName)"
                      class="block w-full rounded-[4px] px-3 py-2 text-left text-[14px] text-ink-1 hover:bg-sunken">
                      {{ opt.providerName }} — {{ opt.feed.name }}
                    </button>
                  }
                }
              </div>
            }
          </div>
        </div>

        @if (assignedFeeds().length === 0) {
          <div class="rounded-control py-6 text-center text-[13px] text-ink-3">{{ t('fields.feedsEmpty') }}</div>
        } @else {
          <div class="flex flex-col">
            @for (row of assignedFeeds(); track row.feedId + '-' + $index; let ri = $index) {
              <div class="flex gap-4 border-t border-hairline py-3.5 first:border-t-0">
                <div class="w-[150px] flex-none">
                  <div class="text-[13px] font-semibold text-ink-1">{{ row.feedName }}</div>
                  <div class="text-[11px] text-ink-3">{{ row.providerName }}</div>
                  <button type="button" (click)="removeFeed(ri)" class="mt-1 text-[12px] font-semibold text-ink-2 hover:text-link">
                    {{ t('fields.removeFeed') }}
                  </button>
                </div>
                <div class="flex flex-1 flex-col gap-1.5">
                <div class="flex flex-wrap items-center gap-2">
                  @for (stop of row.stops; track si; let si = $index) {
                    @let pickedName = stopName(row.feedId, stop);
                    <div class="flex h-9 min-w-[100px] max-w-[232px] items-center gap-1 rounded-control bg-card px-2 shadow-hairline focus-within:ring-2 focus-within:ring-focus"
                      [attr.title]="pickedName">
                      @if (stopComboOptions()[row.feedId]?.length) {
                        <!-- Suggestions by name or id; picking writes the stopId, free typing is still accepted. -->
                        <app-combobox
                          class="flex-1"
                          [ngModel]="stop"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="setStop(ri, si, $event)"
                          [options]="stopComboOptions()[row.feedId]"
                          [placeholder]="t('fields.stopSearchPlaceholder')"
                          [ariaLabel]="t('fields.stopSearchPlaceholder')"
                          [moreLabel]="t('fields.stopMoreMatches')"
                          [noMatchLabel]="t('fields.stopNoMatch')"
                          inputClass="w-full min-w-0 border-none bg-transparent p-0 text-[13px] leading-5 text-ink-1 outline-none placeholder:text-ink-3"
                        />
                      } @else {
                        <input
                          [ngModel]="stop"
                          [ngModelOptions]="{ standalone: true }"
                          (ngModelChange)="setStop(ri, si, $event)"
                          [placeholder]="t('fields.stopPlaceholder')"
                          class="min-w-0 flex-1 border-none bg-transparent p-0 text-[13px] leading-5 text-ink-1 outline-none placeholder:text-ink-3"
                        />
                      }
                      @if (pickedName) {
                        <span class="max-w-[110px] flex-none truncate text-[11px] text-ink-3">{{ pickedName }}</span>
                      }
                      @if (row.stops.length > 1) {
                        <button type="button" (click)="removeStop(ri, si)" class="flex-none text-ink-3 hover:text-danger">&times;</button>
                      }
                    </div>
                  }
                  <button type="button" (click)="addStop(ri)"
                    class="flex h-9 flex-none items-center gap-1 rounded-control border border-hairline bg-card px-3 text-[13px] font-semibold text-ink-1 hover:bg-sunken">
                    {{ t('fields.addStop') }}
                  </button>
                </div>
                @if (isStopListUnavailable(row.feedId)) {
                  <span class="text-[11px] text-ink-3">{{ t('fields.stopListUnavailable') }}</span>
                }
                </div>
              </div>
            }
          </div>
        }
      </ng-template>
    </app-wizard-step>

    <!-- 3 · Location -->
    <app-wizard-step
      id="location"
      [label]="t('steps.location')"
      [sublabel]="t('steps.locationSub')"
      [description]="t('steps.locationDesc')"
      icon="icon-menu-monitor1"
      [control]="locationForm"
    >
      <ng-template>
        <!-- Positions of the assigned stops (from stop discovery), offered as the device location -->
        @if (stopPositions().length) {
          <div class="mb-3 flex flex-wrap items-center gap-2">
            <span class="text-[12px] font-semibold text-ink-2">{{ t('fields.useStopPosition') }}</span>
            @for (p of stopPositions(); track p.stopId) {
              @let current = isAppliedStopPosition(p);
              <button
                type="button"
                (click)="applyStopPosition(p)"
                [attr.aria-pressed]="current"
                [attr.title]="p.stopId"
                class="h-8 rounded-control px-3 text-[13px] font-semibold text-ink-1 shadow-hairline hover:bg-sunken"
                [class.bg-sunken]="current"
              >
                {{ p.name }}
              </button>
            }
          </div>
        }
        @if (locationSource(); as source) {
          @if (source.kind === 'stop') {
            <p class="m-0 mb-3 text-[12px] leading-[18px] text-ink-3">{{ t('fields.locationFromStop', { name: source.name }) }}</p>
          }
        }
        <div class="h-[328px] w-full overflow-hidden rounded-control shadow-hairline">
          <div
            leaflet
            class="h-full w-full"
            [leafletOptions]="mapOptions"
            [leafletLayers]="mapLayers"
            (leafletClick)="onMapClick($event)"
            (leafletMapReady)="onMapReady($event)"
          ></div>
        </div>
        <form [formGroup]="locationForm" class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <app-form-field
            [error]="locationForm.controls.latitude.invalid && locationForm.controls.latitude.touched ? t('errors.latitudeRange') : null"
          >
            <app-number-input
              formControlName="latitude"
              (ngModelChange)="onCoordsInput()"
              [label]="t('fields.latitude')"
              [min]="-90"
              [max]="90"
              [step]="0.000001"
              [invalid]="locationForm.controls.latitude.invalid && locationForm.controls.latitude.touched"
            />
          </app-form-field>
          <app-form-field
            [error]="locationForm.controls.longitude.invalid && locationForm.controls.longitude.touched ? t('errors.longitudeRange') : null"
          >
            <app-number-input
              formControlName="longitude"
              (ngModelChange)="onCoordsInput()"
              [label]="t('fields.longitude')"
              [min]="-180"
              [max]="180"
              [step]="0.000001"
              [invalid]="locationForm.controls.longitude.invalid && locationForm.controls.longitude.touched"
            />
          </app-form-field>
        </form>
      </ng-template>
    </app-wizard-step>

    <!-- 4 · Device configuration -->
    <app-wizard-step
      id="config"
      [label]="t('steps.config')"
      [sublabel]="t('steps.configSub')"
      [description]="t('steps.configDesc')"
      icon="icon-menu-settings1"
    >
      <ng-template>
        @if (deviceConfigurationOptions().length === 0) {
          <div class="rounded-control py-6 text-center text-[13px] text-ink-3">{{ t('fields.noDeviceConfigurations') }}</div>
        } @else {
          <div role="radiogroup" [attr.aria-label]="t('fields.deviceConfiguration')" class="flex flex-col gap-2">
            @for (cfg of deviceConfigurationOptions(); track cfg.id) {
              <button
                type="button"
                role="radio"
                [attr.aria-checked]="configForm.controls.deviceConfigurationId.value === cfg.id"
                (click)="configForm.controls.deviceConfigurationId.setValue(cfg.id)"
                class="flex items-center gap-3 rounded-control p-3.5 text-left shadow-hairline"
                [class.ring-2]="configForm.controls.deviceConfigurationId.value === cfg.id"
                [class.ring-action]="configForm.controls.deviceConfigurationId.value === cfg.id"
              >
                <span
                  class="flex h-5 w-5 flex-none items-center justify-center rounded-pill border"
                  [class.border-action]="configForm.controls.deviceConfigurationId.value === cfg.id"
                  [class.border-hairline]="configForm.controls.deviceConfigurationId.value !== cfg.id"
                >
                  @if (configForm.controls.deviceConfigurationId.value === cfg.id) {
                    <span class="h-2.5 w-2.5 rounded-pill bg-action"></span>
                  }
                </span>
                <span class="min-w-0">
                  <span class="block text-[16px] font-semibold leading-5 text-ink-1">{{ cfg.name }}</span>
                  @if (configurationSpecLine(cfg); as spec) {
                    <span class="block text-[12px] font-semibold leading-[18px] text-ink-3">{{ spec }}</span>
                  }
                </span>
              </button>
            }
          </div>
        }
      </ng-template>
    </app-wizard-step>

    <!-- 5 · Review -->
    <app-wizard-step
      id="review"
      [label]="t('steps.review')"
      [sublabel]="t('steps.reviewSub')"
      [description]="t('steps.reviewDesc')"
      icon="icon-checkmark"
    >
      <ng-template>
        <div class="overflow-hidden rounded-control shadow-hairline">
          <div class="p-5">
            <div class="mb-2.5 flex items-baseline justify-between">
              <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">{{ t('steps.basics') }}</span>
              <button type="button" class="text-[12px] text-link hover:underline" (click)="wizard.goTo('basics')">
                {{ t('review.edit') }}
              </button>
            </div>
            <div class="grid grid-cols-[148px_1fr] gap-y-1.5 text-[13px] leading-5">
              <span class="text-ink-2">{{ t('review.clientId') }}</span>
              <span class="text-ink-1">{{ basicsForm.getRawValue().clientId }}</span>
              @for (location of locationNames; track trackByIndex(i, location); let i = $index) {
                <span class="text-ink-2">{{ t('review.nameRow', { lang: location.lang }) }}</span>
                <span class="text-ink-1">{{ location.text || t('review.noneSelected') }}</span>
              }
            </div>
          </div>

          <div class="border-t border-hairline p-5">
            <div class="mb-2.5 flex items-baseline justify-between">
              <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">{{ t('steps.feeds') }}</span>
              <button type="button" class="text-[12px] text-link hover:underline" (click)="wizard.goTo('feeds')">
                {{ t('review.edit') }}
              </button>
            </div>
            <div class="grid grid-cols-[148px_1fr] gap-y-1.5 text-[13px] leading-5">
              @for (row of assignedFeeds(); track row.feedId) {
                <span class="text-ink-2">{{ row.feedName }}</span>
                <span class="text-ink-1">{{ row.stops.join(', ') || t('review.noStops') }}</span>
              }
            </div>
          </div>

          <div class="border-t border-hairline p-5">
            <div class="mb-2.5 flex items-baseline justify-between">
              <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">{{ t('steps.location') }}</span>
              <button type="button" class="text-[12px] text-link hover:underline" (click)="wizard.goTo('location')">
                {{ t('review.edit') }}
              </button>
            </div>
            <div class="grid grid-cols-[148px_1fr] gap-y-1.5 text-[13px] leading-5">
              <span class="text-ink-2">{{ t('review.coordinates') }}</span>
              <span class="text-ink-1">
                {{ locationForm.getRawValue().latitude.toFixed(5) }}, {{ locationForm.getRawValue().longitude.toFixed(5) }}
              </span>
            </div>
          </div>

          <div class="border-t border-hairline p-5">
            <div class="mb-2.5 flex items-baseline justify-between">
              <span class="text-[12px] font-semibold uppercase tracking-wide text-ink-3">{{ t('steps.config') }}</span>
              <button type="button" class="text-[12px] text-link hover:underline" (click)="wizard.goTo('config')">
                {{ t('review.edit') }}
              </button>
            </div>
            <div class="grid grid-cols-[148px_1fr] gap-y-1.5 text-[13px] leading-5">
              <span class="text-ink-2">{{ t('review.deviceConfiguration') }}</span>
              @if (selectedConfiguration(); as cfg) {
                <span class="text-ink-1">
                  {{ cfg.name }}
                  @if (configurationSpecLine(cfg); as spec) {
                    <span class="text-ink-3"> · {{ spec }}</span>
                  }
                </span>
              } @else {
                <span class="text-ink-1">{{ t('review.noneSelected') }}</span>
              }
            </div>
          </div>
        </div>
      </ng-template>
    </app-wizard-step>
  </app-wizard-modal>
</ng-container>
